import React, { useState, useEffect } from "react";
import { Entry } from "contentful";
import {
  BrandInvestmentBio,
  BrandInvestmentLeadership,
} from "@tiicker/util/lib/contentful/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleChevronRight,
  faCircleChevronLeft,
} from "@fortawesome/sharp-light-svg-icons";
import Modal from "components/Modal/Modal";
import styles from "./Leadership.module.scss";

type Props = {
  leadership: Entry<BrandInvestmentLeadership>;
};

const Leadership: React.FC<Props> = ({ leadership }) => {
  if (!leadership) return null;

  const { title, headerHex, leaderBio } = leadership.fields;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentModalInfo, setCurrentModalInfo] =
    useState<BrandInvestmentBio>();
  const [openModal, setOpenModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile using the medium breakpoint (992px)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 992);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const itemsPerPage = isMobile ? 1 : 3;

  const nextSlide = () => {
    if (currentIndex + itemsPerPage < leaderBio.length) {
      setCurrentIndex(currentIndex + itemsPerPage);
    }
  };

  const prevSlide = () => {
    if (currentIndex - itemsPerPage >= 0) {
      setCurrentIndex(currentIndex - itemsPerPage);
    }
  };

  const bioClicked = (bio: BrandInvestmentBio) => {
    console.log("bioClicked", bio);
    setCurrentModalInfo(bio);
    setOpenModal(true);
  };

  const visibleBios = leaderBio.slice(
    currentIndex,
    currentIndex + itemsPerPage
  );
  const showNextButton = currentIndex + itemsPerPage < leaderBio.length;
  const showPrevButton = currentIndex > 0;

  return (
    <div className={styles.Leadership}>
      <h2
        className={styles.Leadership__title}
        style={{ backgroundColor: headerHex }}
      >
        {title}
      </h2>

      <div className={styles.Leadership__carousel}>
        <button
          onClick={prevSlide}
          className={`${styles.Leadership__navButton} ${styles["Leadership__navButton--prev"]}`}
          disabled={!showPrevButton}
          style={{ color: showPrevButton ? headerHex : undefined }}
        >
          <FontAwesomeIcon icon={faCircleChevronLeft} />
        </button>

        <div className={styles.Leadership__grid}>
          {visibleBios.map((bio, index) => (
            <div
              key={bio.sys.id}
              className={`${styles.Leadership__item} ${
                isMobile && index === 0
                  ? styles["Leadership__item--active"]
                  : ""
              }`}
            >
              <img
                onClick={() => bioClicked(bio.fields)}
                src={bio.fields.image.fields.file.url}
                alt={bio.fields.name}
                className={styles.Leadership__image}
              />
              <h3 className={styles.Leadership__name}>{bio.fields.name}</h3>
              <p className={styles.Leadership__position}>{bio.fields.title}</p>
              <button
                onClick={() => bioClicked(bio.fields)}
                className={styles.Leadership__bioLink}
                style={{ color: headerHex }}
              >
                View Bio
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={nextSlide}
          className={`${styles.Leadership__navButton} ${styles["Leadership__navButton--next"]}`}
          disabled={!showNextButton}
          style={{ color: showNextButton ? headerHex : undefined }}
        >
          <FontAwesomeIcon icon={faCircleChevronRight} />
        </button>
      </div>
      <Modal visible={openModal} onClose={() => setOpenModal(false)}>
        <div className={styles.Leadership__modal}>
          <div className={styles.Leadership__modalImageColumn}>
            <img
              src={currentModalInfo?.image.fields.file.url}
              alt={currentModalInfo?.name}
              className={styles.Leadership__modalImage}
            />
          </div>
          <div className={styles.Leadership__modalColumn}>
            <h3 className={styles.Leadership__modalName}>
              {currentModalInfo?.name}
            </h3>
            <p
              className={styles.Leadership__modalTitle}
              style={{ color: headerHex }}
            >
              {currentModalInfo?.title}
            </p>
            <p className={styles.Leadership__modalBio}>
              {currentModalInfo?.bio}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Leadership;
